import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import "./Calendar.css";

const API_BASE = process.env.REACT_APP_API_URL;
const ORG_ID = "resh-dev";

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    OrgId: ORG_ID,
  };
}

const parseMongoDate = (d) => {
  if (!d) return null;
  if (typeof d === "string") return new Date(d);
  if (d.$date) return new Date(d.$date);
  return null;
};

const STATUS_COLOR = {
  Confirmed: { bg: "#dbeafe", text: "#1e3a8a", bar: "#3b82f6" },
  Pending: { bg: "#fef9c3", text: "#713f12", bar: "#f59e0b" },
  Completed: { bg: "#d1fae5", text: "#065f46", bar: "#10b981" },
  Cancelled: { bg: "#fee2e2", text: "#991b1b", bar: "#ef4444" },
};

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const parseTimeHalf = (evt) => {
  const timeStr = ((evt.function_time || "") + " " + (evt.session || "") + " " + (evt.function_type || "")).toLowerCase();

  if (timeStr.includes("1st off") || timeStr.includes("first off") || timeStr.includes("1st half") || timeStr.includes("first half") || timeStr.includes("morning") || timeStr.includes("fn")) {
    return 1;
  }
  if (timeStr.includes("2nd off") || timeStr.includes("second off") || timeStr.includes("2nd half") || timeStr.includes("second half") || timeStr.includes("evening") || timeStr.includes("night") || timeStr.includes("an")) {
    return 2;
  }

  if (timeStr.includes("am")) return 1;
  if (timeStr.includes("pm")) {
    const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*pm/);
    if (match) {
      const hr = parseInt(match[1], 10);
      if (hr === 12) return 1;
      return 2;
    }
    return 2;
  }

  const match24 = timeStr.match(/(\d{1,2}):(\d{2})/);
  if (match24) {
    const hr = parseInt(match24[1], 10);
    return hr < 13 ? 1 : 2;
  }

  return 1;
};

const getHalvesForDate = (evts = []) => {
  const first = [];
  const second = [];

  evts.forEach((evt, idx) => {
    const half = parseTimeHalf(evt);
    if (half === 1) {
      first.push(evt);
    } else {
      second.push(evt);
    }
  });

  return { first, second };
};

/* ── Booking Detail Drawer ─────────────────────────────────────────── */
function BookingDrawer({ dateKey, bookings, onClose }) {
  const navigate = useNavigate();
  const dateLabel = dateKey
    ? new Date(dateKey + "T00:00:00").toLocaleDateString("en-IN", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    })
    : "";

  const { first: firstHalf, second: secondHalf } = getHalvesForDate(bookings);

  const renderBookingCard = (b) => {
    const colors = STATUS_COLOR[b.status] || { bg: "#f3f4f6", text: "#374151", bar: "#9ca3af" };
    const time = b.function_time || "";
    return (
      <div key={b._id} className="cal-drawer__card">
        <div className="cal-drawer__card-bar" style={{ background: colors.bar }} />
        <div className="cal-drawer__card-body">
          <div className="cal-drawer__card-top">
            <span className="cal-drawer__card-name">{b.client_name}</span>
            <span
              className="cal-drawer__card-badge"
              style={{ background: colors.bg, color: colors.text }}
            >
              {b.status}
            </span>
          </div>
          {time && (
            <div className="cal-drawer__card-time">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              {time}
            </div>
          )}
          <div className="cal-drawer__card-rows">
            {b.function_type && (
              <div className="cal-drawer__card-row">
                <span className="cal-drawer__card-lbl">Type</span>
                <span className="cal-drawer__card-val">{b.function_type}</span>
              </div>
            )}
            {b.package_name && (
              <div className="cal-drawer__card-row">
                <span className="cal-drawer__card-lbl">Package</span>
                <span className="cal-drawer__card-val">{b.package_name}</span>
              </div>
            )}
            {b.package_price && (
              <div className="cal-drawer__card-row">
                <span className="cal-drawer__card-lbl">Price</span>
                <span className="cal-drawer__card-val">
                  {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(b.package_price))}
                </span>
              </div>
            )}
            {b.client_phone && (
              <div className="cal-drawer__card-row">
                <span className="cal-drawer__card-lbl">Phone</span>
                <span className="cal-drawer__card-val">{b.client_phone}</span>
              </div>
            )}
            {b.client_email && (
              <div className="cal-drawer__card-row">
                <span className="cal-drawer__card-lbl">Email</span>
                <span className="cal-drawer__card-val">{b.client_email}</span>
              </div>
            )}
            {b.function_address && (
              <div className="cal-drawer__card-row">
                <span className="cal-drawer__card-lbl">Venue</span>
                <span className="cal-drawer__card-val">{b.function_address}</span>
              </div>
            )}
          </div>

          <div className="cal-drawer__card-actions" style={{ marginTop: "1rem", borderTop: "1px solid var(--color-border)", paddingTop: "0.75rem" }}>
            <button
              type="button"
              className="cal-drawer__card-btn"
              style={{
                width: "100%",
                background: "transparent",
                color: "var(--color-text)",
                border: "1px solid var(--color-border)",
                padding: "0.55rem",
                fontSize: "0.68rem",
                fontWeight: "600",
                cursor: "pointer",
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.4rem"
              }}
              onClick={() => {
                navigate(`/bookings?bookingId=${b._id}`, { state: { bookingId: b._id } });
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-accent)";
                e.currentTarget.style.color = "var(--color-accent)";
                e.currentTarget.style.background = "rgba(201, 169, 110, 0.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-border)";
                e.currentTarget.style.color = "var(--color-text)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <span>View Full Details</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="cal-drawer-overlay" onClick={onClose}>
      <div className="cal-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cal-drawer__header">
          <div>
            <p className="cal-drawer__date">{dateLabel}</p>
            <p className="cal-drawer__count">
              {bookings.length} schedule{bookings.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button className="cal-drawer__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cal-drawer__body">
          {bookings.length === 0 ? (
            <div className="cal-drawer__empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40" style={{ opacity: 0.25 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p>No schedules on this day</p>
            </div>
          ) : (
            <>
              {/* 1st Half Section */}
              <div className="cal-drawer__section cal-drawer__section--1st">
                <div className="cal-drawer__section-head cal-drawer__section-head--1st">
                  <span>First Half (Morning)</span>
                  <span className="cal-drawer__section-badge">{firstHalf.length}</span>
                </div>
                {firstHalf.length === 0 ? (
                  <p className="cal-drawer__section-empty">No first half schedules</p>
                ) : (
                  firstHalf.map(renderBookingCard)
                )}
              </div>

              {/* 2nd Half Section */}
              <div className="cal-drawer__section cal-drawer__section--2nd">
                <div className="cal-drawer__section-head cal-drawer__section-head--2nd">
                  <span>Second Half (Evening)</span>
                  <span className="cal-drawer__section-badge">{secondHalf.length}</span>
                </div>
                {secondHalf.length === 0 ? (
                  <p className="cal-drawer__section-empty">No second half schedules</p>
                ) : (
                  secondHalf.map(renderBookingCard)
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Main Calendar ─────────────────────────────────────────────────── */
export default function Calendar() {
  const today = new Date();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // "month" | "week"
  const [current, setCurrent] = useState({
    year: today.getFullYear(), month: today.getMonth(),
  });
  // For week view – track which week's Sunday
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [drawerKey, setDrawerKey] = useState(null);
  const [toast, setToast] = useState(null);
  const toastRef = useRef();

  const showToast = useCallback((msg, type = "error") => {
    clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // Build a date-range fetch: two AND conditions using GTE + LTE
  const fetchBookings = useCallback(async (dateFrom, dateTo) => {
    setLoading(true);
    try {
      const body = {
        start: 0,
        end: 500,
        filter: [
          {
            clause: "AND",
            conditions: [
              {
                column: "function_date",
                operator: "GREATERTHANOREQUAL",
                type: "date",
                value: dateFrom,
              },
            ],
          },
          {
            clause: "AND",
            conditions: [
              {
                column: "function_date",
                operator: "LESSTHANOREQUAL",
                type: "date",
                value: dateTo,
              },
            ],
          },
          // {
          //   clause: "AND",
          //   conditions: [
          //     {
          //       column: "status",
          //       operator: "NOTIN",
          //       type: "string",
          //       value: ["Pending", "Cancelled"],
          //     },
          //   ],
          // },
        ],
        sort: [{ colId: "function_date", sort: "asc" }],
        first_filter_append: true,
      };


      const res = await fetch(`${API_BASE}/dataset/data/getbookings`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load schedules.");
      const list = data?.data?.[0]?.response ?? [];
      setBookings(list);
    } catch (err) { showToast(err.message); }
    finally { setLoading(false); }
  }, [showToast]);

  // Re-fetch whenever the viewed period changes (month navigation or week navigation)
  useEffect(() => {
    let dateFrom, dateTo;
    if (view === "month") {
      // First moment of the month → last moment of the month
      dateFrom = new Date(current.year, current.month, 1, 0, 0, 0, 0).toISOString();
      dateTo = new Date(current.year, current.month + 1, 0, 23, 59, 59, 999).toISOString();
    } else {
      // Week view: Sunday 00:00:00 → Saturday 23:59:59
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      dateFrom = new Date(weekStart).toISOString();
      dateTo = weekEnd.toISOString();
    }
    fetchBookings(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.year, current.month, view, weekStart]);

  useEffect(() => {
    return () => clearTimeout(toastRef.current);
  }, []);


  /* bookingMap: "YYYY-MM-DD" -> schedule[] */
  // schedules use `function_date` (ISO string) for the event date
  const bookingMap = {};
  bookings.forEach((b) => {
    const d = parseMongoDate(b.function_date);
    if (!d || isNaN(d)) return;
    const key = toDateKey(d);
    if (!bookingMap[key]) bookingMap[key] = [];
    bookingMap[key].push(b);
  });

  /* ── Navigation ── */
  const goToday = () => {
    setCurrent({ year: today.getFullYear(), month: today.getMonth() });
    const d = new Date(today);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    setWeekStart(d);
  };

  const prevPeriod = () => {
    if (view === "month") {
      setCurrent(c => {
        const m = c.month === 0 ? 11 : c.month - 1;
        const y = c.month === 0 ? c.year - 1 : c.year;
        return { year: y, month: m };
      });
    } else {
      setWeekStart(w => {
        const d = new Date(w);
        d.setDate(d.getDate() - 7);
        return d;
      });
    }
  };

  const nextPeriod = () => {
    if (view === "month") {
      setCurrent(c => {
        const m = c.month === 11 ? 0 : c.month + 1;
        const y = c.month === 11 ? c.year + 1 : c.year;
        return { year: y, month: m };
      });
    } else {
      setWeekStart(w => {
        const d = new Date(w);
        d.setDate(d.getDate() + 7);
        return d;
      });
    }
  };

  /* ── Stats ── */
  const totalThisMonth = bookings.filter(b => {
    const d = parseMongoDate(b.function_date);
    return d && d.getMonth() === current.month && d.getFullYear() === current.year;
  }).length;
  const totalConfirmed = bookings.filter(b => b.status === "Confirmed").length;
  const totalCompleted = bookings.filter(b => b.status === "Completed").length;
  const totalUpcoming = bookings.filter(b => {
    const d = parseMongoDate(b.function_date);
    return d && d >= today;
  }).length;

  /* ── Month view cells ── */
  const firstDayOfMonth = new Date(current.year, current.month, 1).getDay();
  const daysInMonth = new Date(current.year, current.month + 1, 0).getDate();
  const monthCells = [];
  for (let i = 0; i < firstDayOfMonth; i++) monthCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) monthCells.push(d);

  /* ── Week view days ── */
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const weekLabel = (() => {
    const s = weekDays[0];
    const e = weekDays[6];
    if (s.getMonth() === e.getMonth())
      return `${s.getDate()} – ${e.getDate()} ${MONTHS[s.getMonth()]} ${s.getFullYear()}`;
    return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
  })();

  const navLabel = view === "month"
    ? `${MONTHS[current.month]} ${current.year}`
    : weekLabel;

  /* ── Chip component ── */
  const EventChip = ({ booking, compact }) => {
    const colors = STATUS_COLOR[booking.status] || { bg: "#f3f4f6", text: "#374151" };
    const time = booking.function_time || "";
    return (
      <div
        className={`cal-chip${compact ? " cal-chip--compact" : ""}`}
        style={{ background: colors.bg, color: colors.text }}
        title={`${booking.client_name} · ${time || "No time"} · ${booking.function_type || ""}`}
      >
        {time && (
          <span className="cal-chip__time">{time}</span>
        )}
        <span className="cal-chip__name">{booking.client_name}</span>
      </div>
    );
  };

  const MAX_CHIPS_MONTH = 3;

  return (
    <AdminLayout>
      <div className={`cal-page ${view === "week" ? "cal-page--week" : ""}`}>

        {/* ── Header ── */}
        <header className="cal-page__header">
          <div className="cal-page__header-left">
            <h2 className="cal-page__title">Shoot Schedule</h2>
          </div>

          <div className="cal-page__header-center">
            {/* Stats */}
            <div className="cal-stats">
              <div className="cal-stat">
                <span className="cal-stat__val">{totalThisMonth}</span>
                <span className="cal-stat__lbl">This Month</span>
              </div>
              <div className="cal-stat">
                <span className="cal-stat__val">{totalConfirmed}</span>
                <span className="cal-stat__lbl">Confirmed</span>
              </div>
              <div className="cal-stat">
                <span className="cal-stat__val">{totalCompleted}</span>
                <span className="cal-stat__lbl">Completed</span>
              </div>
              <div className="cal-stat">
                <span className="cal-stat__val">{totalUpcoming}</span>
                <span className="cal-stat__lbl">Upcoming</span>
              </div>
            </div>
          </div>

          <div className="cal-page__header-right">
            {/* View toggle */}
            <div className="cal-view-toggle">
              <button
                className={`cal-view-btn${view === "month" ? " cal-view-btn--active" : ""}`}
                onClick={() => setView("month")}
              >
                Month
              </button>
              <button
                className={`cal-view-btn${view === "week" ? " cal-view-btn--active" : ""}`}
                onClick={() => setView("week")}
              >
                Week
              </button>
            </div>
          </div>
        </header>

        {/* ── Calendar card ── */}
        <div className="cal-card">

          {/* Nav bar */}
          <div className="cal-nav">
            <div className="cal-nav__left">
              <button className="cal-nav__btn" onClick={prevPeriod} aria-label="Previous">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button className="cal-nav__btn" onClick={nextPeriod} aria-label="Next">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <span className="cal-nav__label">{navLabel}</span>
            </div>
            <div className="cal-nav__right">
              <button className="cal-today-btn" onClick={goToday}>Today</button>
            </div>
          </div>

          {loading ? (
            <div className="cal-loading">
              <div className="app-spinner" />
              <span>Loading schedules…</span>
            </div>
          ) : view === "month" ? (

            /* ══════════ MONTH VIEW ══════════ */
            <>
              {/* Day headers */}
              <div className="cal-month-head">
                {DAYS_SHORT.map(d => (
                  <div key={d} className="cal-day-head">{d}</div>
                ))}
              </div>

              {/* Cells */}
              <div className="cal-month-grid">
                {monthCells.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} className="cal-cell cal-cell--empty" />;

                  const key = `${current.year}-${String(current.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const evts = bookingMap[key] || [];
                  const { first: firstHalf, second: secondHalf } = getHalvesForDate(evts);
                  const isToday = day === today.getDate() && current.month === today.getMonth() && current.year === today.getFullYear();
                  const hasFirst = firstHalf.length > 0;
                  const hasSecond = secondHalf.length > 0;
                  const bgClass = hasFirst && hasSecond ? " cal-cell--has-both" : hasFirst ? " cal-cell--has-1st" : hasSecond ? " cal-cell--has-2nd" : "";

                  return (
                    <div
                      key={key}
                      className={`cal-cell${isToday ? " cal-cell--today" : ""}${bgClass}`}
                      onClick={() => setDrawerKey(key)}
                    >
                      <div className="cal-cell__top">
                        <span className="cal-cell__day">{day}</span>
                        {evts.length > 0 && (
                          <span className="cal-cell__count">{evts.length}</span>
                        )}
                      </div>

                      <div className="cal-cell__zones">
                        <div className="cal-cell__zone cal-cell__zone--1st">
                          {firstHalf.slice(0, 2).map((b, j) => (
                            <EventChip key={j} booking={b} compact />
                          ))}
                          {firstHalf.length > 2 && (
                            <div className="cal-cell__more">+{firstHalf.length - 2} more</div>
                          )}
                        </div>

                        <div className="cal-cell__zone cal-cell__zone--2nd">
                          {secondHalf.slice(0, 2).map((b, j) => (
                            <EventChip key={j} booking={b} compact />
                          ))}
                          {secondHalf.length > 2 && (
                            <div className="cal-cell__more">+{secondHalf.length - 2} more</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>

          ) : (

            /* ══════════ WEEK VIEW ══════════ */
            <>
              {/* Day headers */}
              <div className="cal-week-head">
                <div className="cal-week-head__spacer" />
                {weekDays.map((d, i) => {
                  const isTodayCol = toDateKey(d) === toDateKey(today);
                  return (
                    <div
                      key={i}
                      className={`cal-week-day-head${isTodayCol ? " cal-week-day-head--today" : ""}`}
                    >
                      <span className="cal-week-day-head__short">{DAYS_SHORT[d.getDay()]}</span>
                      <span className={`cal-week-day-head__num${isTodayCol ? " cal-week-day-head__num--today" : ""}`}>
                        {d.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* All-day events row */}
              <div className="cal-week-allday">
                <div className="cal-week-allday__label">Schedules</div>
                {weekDays.map((d, i) => {
                  const key = toDateKey(d);
                  const evts = bookingMap[key] || [];
                  const { first: firstHalf, second: secondHalf } = getHalvesForDate(evts);
                  const isTodayCol = key === toDateKey(today);
                  const hasFirst = firstHalf.length > 0;
                  const hasSecond = secondHalf.length > 0;
                  const bgClass = hasFirst && hasSecond ? " cal-week-allday__cell--has-both" : hasFirst ? " cal-week-allday__cell--has-1st" : hasSecond ? " cal-week-allday__cell--has-2nd" : "";

                  return (
                    <div
                      key={i}
                      className={`cal-week-allday__cell${isTodayCol ? " cal-week-allday__cell--today" : ""}${bgClass}`}
                      onClick={() => setDrawerKey(key)}
                    >
                      <div className="cal-week-zone cal-week-zone--1st">
                        {firstHalf.slice(0, 2).map((b, j) => (
                          <EventChip key={j} booking={b} />
                        ))}
                        {firstHalf.length > 2 && (
                          <div className="cal-cell__more">+{firstHalf.length - 2} more</div>
                        )}
                      </div>
                      <div className="cal-week-zone cal-week-zone--2nd">
                        {secondHalf.slice(0, 2).map((b, j) => (
                          <EventChip key={j} booking={b} />
                        ))}
                        {secondHalf.length > 2 && (
                          <div className="cal-cell__more">+{secondHalf.length - 2} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Legend */}
          <div className="cal-legend">
            {Object.entries(STATUS_COLOR).map(([s, c]) => (
              <span key={s} className="cal-legend__item">
                <span className="cal-legend__dot" style={{ background: c.bar }} />
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Drawer */}
        {drawerKey && (
          <BookingDrawer
            dateKey={drawerKey}
            bookings={bookingMap[drawerKey] || []}
            onClose={() => setDrawerKey(null)}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className="bookings-toast-container">
            <div className={`bookings-toast bookings-toast--${toast.type}`}>
              {toast.type === "success" ? "✓" : "✕"} {toast.msg}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
